/**
 * Expanded order view, inspector-style detail sheet following the conventions
 * in docs/expandable-index-pages.md: identity header with actions, flag chips,
 * key facts, Customer / Shipping / Payment / Fulfillment sections, line items,
 * totals, and a tabbed activity region (history / packages / returns /
 * escalations / emails).
 *
 * This is a partner-facing subset of reware-admin's order card, not a copy of
 * it. Trove-internal fields (catalogAmount, shippingBase,
 * is3plOrderNotificationSent) and the item-level operator actions behind
 * canBeRemoved / canBeFlagged / canClearProblem / canMarkAllMissingItems are
 * selected by the query but intentionally not surfaced here.
 */
import { useRecordContext } from 'react-admin';
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { CopyButton, displayNameFor, semanticStateColor } from '@src/components/indexPage';

import OrderActions from './OrderActions';
import OrderActivityTabs from './OrderActivityTabs';
import {
  CustomerSection,
  FulfillmentSection,
  OrderFlags,
  OrderKeyFacts,
  PaymentSection,
  ShippingSection,
} from './OrderDetailSections';
import OrderItems from './OrderItems';
import OrderTotals from './OrderTotals';
import type { ConstantOption, OrderRecord } from './types';

interface OrderExpandProps {
  states: ConstantOption[];
  conditions: ConstantOption[];
  refundReasons: ConstantOption[];
  giftcardReasons: ConstantOption[];
  returnReasons: ConstantOption[];
  freeShippingReasons: ConstantOption[];
}

export const OrderExpand = ({
  states,
  conditions,
  refundReasons,
  giftcardReasons,
  returnReasons,
  freeShippingReasons,
}: OrderExpandProps) => {
  const order = useRecordContext<OrderRecord>();
  if (!order) return null;

  const orderNumber = order.uuid.split('-')[0].toUpperCase();
  const platformLabel = order.commercePlatform === 'shopify' ? 'Shopify' : order.commercePlatform === 'legacy' ? 'Trove' : order.commercePlatform;

  return (
    <Box sx={{ bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', p: 2.5 }}>
      <Stack spacing={2}>
        {/* Identity header + actions */}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontSize: 16, lineHeight: 1.3 }}>
                {`Order ${orderNumber}`}
              </Typography>
              <Chip size="small" color={semanticStateColor(order.state)} label={displayNameFor(states, order.state)} />
              {platformLabel && <Chip size="small" variant="outlined" label={platformLabel} />}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', mt: 0.5, color: 'text.secondary' }}>
              <Typography variant="caption">
                ID
                {' '}
                {order.id}
                <CopyButton name="Order ID" value={String(order.id)} />
              </Typography>
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {order.uuid}
                <CopyButton name="Order UUID" value={order.uuid} />
              </Typography>
              {order.paymentUrl && (
                <Link href={order.paymentUrl} target="_blank" rel="noopener" variant="caption" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  {`${order.paymentVendor ?? 'Payment'} ${order.paymentId ?? ''}`.trim()}
                  <OpenInNewIcon sx={{ fontSize: 12, ml: 0.25 }} />
                </Link>
              )}
              {order.externalOrderNumber && (
                <Typography variant="caption">{`External ${order.externalOrderNumber}`}</Typography>
              )}
            </Stack>
          </Box>
          <OrderActions
            order={order}
            refundReasons={refundReasons}
            giftcardReasons={giftcardReasons}
            returnReasons={returnReasons}
            freeShippingReasons={freeShippingReasons}
          />
        </Stack>

        <OrderFlags order={order} />

        {order.orderRecentWebhookEventError && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{ bgcolor: 'error.light', border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1.5, py: 1, opacity: 0.95 }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 16, mt: '2px', color: 'error.main' }} />
            <Typography variant="body2" sx={{ color: 'error.dark' }}>
              {order.orderRecentWebhookEventError}
            </Typography>
          </Stack>
        )}

        <OrderKeyFacts order={order} states={states} />

        {/* Detail sections */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(230px, 1fr))' },
            columnGap: 4,
            rowGap: 2.5,
            alignContent: 'start',
          }}
        >
          <CustomerSection order={order} />
          <ShippingSection order={order} />
          <PaymentSection order={order} />
          <FulfillmentSection order={order} />
        </Box>

        <OrderItems order={order} conditions={conditions} />

        <OrderTotals order={order} />

        <OrderActivityTabs order={order} />
      </Stack>
    </Box>
  );
};

export default OrderExpand;
